<script lang="ts">
import { useRouter } from 'vue-router'
import { defineComponent } from 'vue'
import PageBreadcrumb from '@/components/common/PageBreadcrumb.vue'
import ReadyMadeCompanyDataService from '@/services/ReadyMadeCompanyDataService'
import type ReadyMadeCompany from '@/types/ReadyMadeCompany'
import type ResponseData from '@/types/ResponseData'
import type { LaravelPaginatedResponse } from '@/types/ApiResponses'
import * as _ from 'lodash'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import { Table, TableHeader, TableBody, TableRow, TableCell } from '@/components/ui/table'
import { TableCard, TableSearch, TablePagination, TableEmptyState } from '@/components/common/table'
import { Button } from '@/components/ui/button'
import { Pencil, Plus, Trash2 } from 'lucide-vue-next'

export default defineComponent({
  name: 'ready-made-company-list',
  components: {
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableCell,
    TableCard,
    TableSearch,
    TablePagination,
    TableEmptyState,
    Button,
    Pencil,
    Plus,
    Trash2,
    PageBreadcrumb,
  },
  data() {
    return {
      companies: [] as ReadyMadeCompany[],
      filterCompanies: '',
      loaded: false,
      pagination: {} as any,
      showDeleteModal: false,
      companyToDelete: null as ReadyMadeCompany | null,
      router: useRouter(),
    }
  },
  methods: {
    retrieveCompanies(page: number, type: string) {
      if (type === 'prev') {
        page--
      } else if (type === 'next') {
        page++
      }

      ReadyMadeCompanyDataService.getAll(page)
        .then((response: ResponseData) => {
          this.companies = response.data.data.data
          this.pagination = response.data.data
          this.loaded = true
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
          this.loaded = true
        })
    },

    refreshList() {
      this.retrieveCompanies(1, '')
    },

    addCompany() {
      this.router.push({ name: 'Pridať ready made firmu' })
    },

    confirmDelete(company: ReadyMadeCompany) {
      this.companyToDelete = company
      this.showDeleteModal = true
    },

    cancelDelete() {
      this.showDeleteModal = false
      this.companyToDelete = null
    },

    async deleteCompany() {
      if (!this.companyToDelete?.id) return

      try {
        await ReadyMadeCompanyDataService.delete(this.companyToDelete.id)
        toast.success('Firma bola úspešne zmazaná')
        this.showDeleteModal = false
        this.companyToDelete = null
        this.refreshList()
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Neznáma chyba'
        toast.error('Chyba pri mazaní firmy: ' + errorMessage)
      }
    },

    formatPrice(price: number | null | undefined): string {
      if (price == null) return '—'
      return price.toFixed(2) + ' €'
    },

    formatDate(date: string): string {
      if (!date) return '—'
      return new Date(date).toLocaleDateString('sk-SK')
    },
  },
  computed: {
    filteredCompanies() {
      return this.companies.filter((c) => {
        const search = this.filterCompanies.toLowerCase()
        return (
          c.company_name.toLowerCase().includes(search) ||
          c.ico.toLowerCase().includes(search) ||
          c.headquarters.toLowerCase().includes(search)
        )
      })
    },
  },
  mounted() {
    this.retrieveCompanies(1, '')
  },
})
</script>

<template>
  <main class="flex-1">
    <div class="py-6">
      <div class="max-w-(--breakpoint-2xl) mx-auto">
        <PageBreadcrumb page-title="Ready made firmy" />
      </div>
      <TableCard title="Ready made firmy">
        <template #controls>
          <div class="flex w-full items-center gap-3 sm:w-auto">
            <div class="flex-1 sm:w-64 sm:flex-initial md:w-72">
              <TableSearch v-model="filterCompanies" />
            </div>
            <Button class="shrink-0" @click="addCompany()">
              <Plus />
              <span>Pridať firmu</span>
            </Button>
          </div>
        </template>
          <div class="max-w-full overflow-x-auto min-w-0">
            <Table class="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
              <TableHeader
                class="border-y border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900"
                >
                  <TableRow>
                    <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Názov firmy</TableCell>
                    <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">IČO</TableCell>
                    <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Sídlo</TableCell>
                    <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Platca DPH</TableCell>
                    <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Dátum vzniku</TableCell>
                    <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Cena</TableCell>
                    <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Zľavnená cena</TableCell>
                    <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 text-center">Akcie</TableCell>
                  </TableRow>
                </TableHeader>
                    <TableBody class="divide-y divide-gray-200 dark:divide-gray-800">
                      <TableRow class="hover:bg-gray-200 dark:hover:bg-gray-700" v-for="company in filteredCompanies" :key="company.id">
                        <TableCell class="px-6 py-4 text-sm font-medium text-gray-800 dark:text-white/90">{{ company.company_name }}</TableCell>
                        <TableCell class="px-6 py-4 text-sm text-gray-800 dark:text-white/90">{{ company.ico }}</TableCell>
                        <TableCell class="px-6 py-4 text-sm text-gray-800 dark:text-white/90">{{ company.headquarters }}</TableCell>
                        <TableCell class="px-6 py-4 text-sm text-gray-800 dark:text-white/90">
                          <span v-if="company.vat_payer" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Áno</span>
                          <span v-else class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-white/90">Nie</span>
                        </TableCell>
                        <TableCell class="px-6 py-4 text-sm text-gray-800 dark:text-white/90">{{ formatDate(company.founding_date) }}</TableCell>
                        <TableCell class="px-6 py-4 text-sm text-gray-800 dark:text-white/90">{{ formatPrice(company.price) }}</TableCell>
                        <TableCell class="px-6 py-4 text-sm text-gray-800 dark:text-white/90">{{ formatPrice(company.sale_price) }}</TableCell>
                        <TableCell class="px-6 py-4 text-sm font-medium">
                          <div class="flex items-center justify-center space-x-2">
                            <Button as-child size="xs">
                              <router-link :to="'/ready-made-company-detail/' + company.id">
                                <Pencil class="size-4" />
                                <span>Upraviť</span>
                              </router-link>
                            </Button>
                            <Button variant="destructive" size="xs" @click="confirmDelete(company)">
                              <Trash2 class="size-4" />
                              <span>Zmazať</span>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                    <TableEmptyState
                      v-if="loaded && !companies.length"
                      title="Žiadne firmy neboli nájdené"
                      hint="Skúste upraviť hľadanie."
                      colspan="8"
                    />
                  </Table>
                </div>
                <template #footer>
                  <TablePagination
                    v-if="pagination.last_page > 1"
                    :current-page="pagination.current_page"
                    :total-pages="pagination.last_page"
                    @page-change="retrieveCompanies($event, '')"
                  />
                </template>
              </TableCard>
            </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="fixed inset-0 bg-gray-600/50 overflow-y-auto h-full w-full z-50 dark:bg-gray-900"
      @click="cancelDelete"
    >
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-white/3" @click.stop>
        <div class="mt-3 text-center">
          <div class="mx-auto flex items-center justify-center size-12 rounded-full bg-red-100">
            <svg class="size-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 class="text-lg leading-6 font-medium text-gray-900 mt-4 dark:text-white/90">Potvrdiť vymazanie</h3>
          <div class="mt-2 px-7 py-3">
            <p class="text-sm text-gray-500 dark:text-gray-400">
              Naozaj chcete zmazať firmu <strong>"{{ companyToDelete?.company_name }}"</strong>?<br />
              Táto akcia sa nedá vrátiť.
            </p>
          </div>
          <div class="items-center px-4 py-3">
            <div class="flex space-x-3">
              <button
                @click="cancelDelete"
                class="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md w-full shadow-xs hover:bg-gray-600 dark:bg-gray-700 dark:hover:bg-white/5"
              >
                Zrušiť
              </button>
              <button
                @click="deleteCompany"
                class="px-4 py-2 bg-red-500 text-white text-base font-medium rounded-md w-full shadow-xs hover:bg-red-600"
              >
                Zmazať
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>
